import { useState, useRef, useEffect } from 'react'
import { useAppStore } from '../../store/appStore'
import { useAuthStore } from '../../auth/useAuthStore'
import { apiUrl, authFetch } from '../../lib/api'
import { NotificationBell } from '../Notifications/NotificationBell'

interface TitleBarProps {
  onOpenSettings?: () => void
  onOpenCommandPalette?: () => void
  onOpenFileSearch?: () => void
  onBackToDashboard?: () => void
}

export function TitleBar({ onOpenSettings, onOpenCommandPalette, onOpenFileSearch, onBackToDashboard }: TitleBarProps) {
  const { openTabs, activeTabId, closeTab, setActiveTab, saveTab, autoSave, toggleAutoSave } = useAppStore()
  const activeTab = openTabs.find((t) => t.id === activeTabId)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showMenu) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showMenu])

  const initials = user?.name
    ? user.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : '?'

  const planColors: Record<string, string> = {
    free: 'var(--text-2)', pro: 'var(--accent)', unlimited: 'var(--accent-3)',
  }

  return (
    <div className="titlebar">
      <div className="titlebar__logo">
        {onBackToDashboard && (
          <button onClick={onBackToDashboard} title="Back to Dashboard" style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 12, padding: '2px 6px', borderRadius: 4, marginRight: 4 }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-0)'; e.currentTarget.style.background = 'var(--bg-3)' }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.background = 'none' }}
          >←</button>
        )}
        <span className="pulse-dot" style={{ width: 8, height: 8 }} />
        <span className="titlebar__logo-text">PULSE</span>
      </div>

      <div className="titlebar__tabs">
        {openTabs.map((tab) => (
          <div
            key={tab.id}
            className={`titlebar__tab ${activeTabId === tab.id ? 'titlebar__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="titlebar__tab-lang" data-lang={tab.language} />
            <span className="titlebar__tab-name">{tab.name}</span>
            {tab.isDirty && <span className="titlebar__tab-dirty" title="Unsaved changes" />}
            <button
              className="titlebar__tab-close"
              onClick={(e) => { e.stopPropagation(); closeTab(tab.id) }}
              title="Close"
            >✕</button>
          </div>
        ))}
        {openTabs.length === 0 && (
          <div className="titlebar__tab titlebar__tab--empty">
            <span style={{ color: 'var(--text-2)', fontSize: 11 }}>No files open</span>
          </div>
        )}
      </div>

      <div className="titlebar__actions">
        <button
          className="titlebar__icon-btn"
          title="Save (Ctrl+S)"
          onClick={() => activeTab && saveTab(activeTab.id)}
          style={{ color: activeTab?.isDirty ? 'var(--accent-2)' : undefined, opacity: activeTab?.isDirty ? 1 : 0.4 }}
          disabled={!activeTab?.isDirty}
        >💾</button>

        <button
          className="titlebar__icon-btn"
          title={autoSave ? 'Auto-save ON' : 'Auto-save OFF'}
          onClick={toggleAutoSave}
          style={{
            fontSize: 9, fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '0.04em',
            color: autoSave ? 'var(--accent-2)' : 'var(--text-2)',
            background: autoSave ? 'rgba(95,212,164,0.12)' : undefined,
            border: autoSave ? '1px solid rgba(95,212,164,0.25)' : '1px solid transparent',
            borderRadius: 6, width: 'auto', padding: '0 6px',
          }}
        >AUTO</button>

        <button className="titlebar__icon-btn" title="File Search (Ctrl+P)" onClick={onOpenFileSearch}>⌕</button>
        <button className="titlebar__icon-btn" title="Command Palette (Ctrl+K)" onClick={onOpenCommandPalette}>⌘</button>
        <NotificationBell />
        <DeployButton />
        <NotificationBell />
        <button className="titlebar__icon-btn" title="Settings" onClick={onOpenSettings}>⚙</button>

        {/* User avatar */}
        <div style={{ position: 'relative', marginLeft: 4 }} ref={menuRef}>
          <button
            className="titlebar__icon-btn"
            onClick={() => setShowMenu((p) => !p)}
            style={{
              width: 28, height: 28, borderRadius: '50%', fontSize: 10,
              background: 'var(--accent)', color: '#fff', fontWeight: 700,
              fontFamily: 'var(--font-display)', letterSpacing: '0.02em',
              border: showMenu ? '2px solid var(--accent-2)' : '2px solid transparent',
            }}
            title={user?.name ?? 'Account'}
          >{initials}</button>

          {showMenu && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 200,
              background: 'var(--bg-1)', border: '1px solid var(--border-2)',
              borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              minWidth: 200, overflow: 'hidden',
            }}>
              {/* User info */}
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-0)', fontFamily: 'var(--font-mono)' }}>
                  {user?.name}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                  {user?.email}
                </div>
                <span style={{
                  display: 'inline-block', marginTop: 6, fontSize: 8, fontWeight: 900,
                  fontFamily: 'var(--font-display)', letterSpacing: '0.1em',
                  padding: '2px 8px', borderRadius: 8,
                  color: planColors[user?.plan ?? 'free'],
                  border: `1px solid ${planColors[user?.plan ?? 'free']}40`,
                  background: `${planColors[user?.plan ?? 'free']}15`,
                  textTransform: 'uppercase',
                }}>
                  {user?.plan ?? 'free'}
                </span>
              </div>

              {/* Menu items */}
              <MenuItem label="⚙ Settings" onClick={() => { setShowMenu(false); onOpenSettings?.() }} />
              <MenuItem label="◎ Goals" onClick={() => { setShowMenu(false) }} />
              <div style={{ height: 1, background: 'var(--border)' }} />
              <MenuItem label="↪ Sign Out" onClick={() => { setShowMenu(false); logout() }} danger />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function MenuItem({ label, onClick, danger }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '8px 16px', background: 'none', border: 'none',
        color: danger ? 'var(--red)' : 'var(--text-1)', cursor: 'pointer',
        fontFamily: 'var(--font-mono)', fontSize: 11, textAlign: 'left',
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-3)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
    >{label}</button>
  )
}

function DeployButton() {
  const [deploying, setDeploying] = useState(false)
  const [result, setResult] = useState<{ status: string; url?: string } | null>(null)

  const handleDeploy = async () => {
    setDeploying(true)
    setResult(null)
    try {
      const res = await authFetch(apiUrl('/api/deploy'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const data = await res.json()
      setResult(data)
      if (data.url) {
        setTimeout(() => setResult(null), 5000)
      }
    } catch {
      setResult({ status: 'failed' })
    } finally {
      setDeploying(false)
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        className="titlebar__icon-btn"
        title="Deploy project"
        onClick={handleDeploy}
        disabled={deploying}
        style={{
          color: deploying ? 'var(--accent-3)' : result?.status === 'deployed' ? 'var(--accent-2)' : undefined,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 16 12 12 8 16" />
          <line x1="12" y1="12" x2="12" y2="21" />
          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
        </svg>
      </button>
      {result && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 200,
          background: 'var(--bg-1)', border: '1px solid var(--border-2)',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          padding: '10px 14px', minWidth: 200,
          fontFamily: 'var(--font-mono)', fontSize: 11,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: result.status === 'deployed' ? 'var(--accent-2)' : 'var(--red)',
            }} />
            <span style={{ color: 'var(--text-0)', fontWeight: 700 }}>
              {result.status === 'deployed' ? 'Deployed!' : 'Deploy failed'}
            </span>
          </div>
          {result.url && (
            <a
              href={apiUrl(result.url)}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--accent)', fontSize: 10, wordBreak: 'break-all' }}
            >
              Open preview
            </a>
          )}
        </div>
      )}
    </div>
  )
}
